class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // If search params contains cart-open, open the cart drawer
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('cart-open')) {
      const cartLink = document.querySelector('#cart-icon-bubble');
      this.open(cartLink);
    }

    this.drawerAnimation = new DrawerAnimation(
      this,
      this.querySelector('.drawer__inner'),
      {
        type: 'drawer',
        drawerPosition: 'right',
        onOpen: () => {
          this.header.classList.add('header--has-scrolled');

          // Wait for the slide-in animation to complete (500ms) before setting focus
          // This ensures focus is visible and announced properly to screen readers
          setTimeout(() => {
            // The empty state only exists while the cart is empty, so fall back to the
            // drawer itself: after an add it has been replaced by the line items.
            const containerToTrapFocusOn =
              (this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty')) ||
              document.getElementById('CartDrawer');
            // Focus the close button for better accessibility - it's the first interactive
            // element and ensures screen reader users know the drawer is open
            const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
            if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
          }, 500);
        },
        onClose: () => {
          removeTrapFocus(this.activeElement);

          const headerWrapper = document.querySelector('.section-header');
          if (!headerWrapper.classList.contains('scrolled-past-header')) {
            this.header.classList.remove('header--has-scrolled');
          }
        }
      }
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Close the mobile nav drawer if open, so the cart isn't rendered behind an inert overlay
    if (document.body.classList.contains('state--nav-open')) {
      document.querySelector('.js-drawerToggle')?.click();
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Use the drawer animation utility
    if (this.drawerAnimation) {
      this.drawerAnimation.open();
    } else {
      // Fallback if animation isn't initialized yet
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      this.header.classList.add('header--has-scrolled');
    }
  }

  close() {
    // Use the drawer animation utility
    if (this.drawerAnimation) {
      this.drawerAnimation.close();
    } else {
      // Fallback if animation isn't initialized yet
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');

      const headerWrapper = document.querySelector('.section-header');
      if (!headerWrapper.classList.contains('scrolled-past-header')) {
        this.header.classList.remove('header--has-scrolled');
      }

      removeTrapFocus(this.activeElement);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // The empty-cart class is rendered on <cart-drawer> itself (and on the
    // <cart-drawer-items> inside it, which the re-render below replaces). The host
    // element outlives the re-render, so it has to be cleared here: otherwise the
    // first add to an empty cart renders the line items and then hides them
    // (.is-empty .cart__contents in component-cart.css), and open() goes looking for
    // a .drawer__inner-empty that the re-render has just removed.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    window.Klarna?.OnsiteMessaging?.refresh();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      setTimeout(() => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer').querySelector(".drawer__inner");
        const focusElement = this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      }, 300)
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: this.id,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: this.id,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
